import { Divider, Empty, Icon, Input, Spin, Radio } from 'antd';
import classNames from 'classnames';
import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useArrayState } from '../../../hooks/useArrayState';
import useOuterClickListener from '../../../hooks/useOuterClickListener';
import DispexButton from '../../DispexButton/DispexButton';
import styles from './DropdownRender.module.scss';

const DropdownRender = ({
  options,
  setState,
  selected,
  closeDropdown,
  loading,
  textField,
  customOptionRender,
  hideSearch,
  searchFilter,
  searchRequest,
  emptyText,
  style,
  selectAllAvailable = true,
  deselectAllAvailable = true,
  radioGroupData = null,
  singleSelect = false,
  searchInputAutoFocus = false,
}) => {
  const dropdownWindowRef = useRef();

  const [preSelected, preAddItem, preDeleteItem, setPreState] = useArrayState(
    selected,
    { singleSelect }
  );

  const [search, setSearch] = useState('');
  const [shownOptions, setShownOptions] = useState([]);

  const onSearchChange = useCallback((e) => setSearch(e.target.value));

  const onSubmit = useCallback(
    (e) => {
      e.stopPropagation();
      setState(preSelected);
      closeDropdown();
    },
    [preSelected, closeDropdown, setState]
  );

  useOuterClickListener(dropdownWindowRef, closeDropdown);

  useEffect(() => {
    setShownOptions(options);
  }, [options]);

  const defaultFilter = useCallback(
    (option) =>
      option[textField]?.toLowerCase().includes(search?.toLowerCase()),
    [textField, search]
  );

  useEffect(() => {
    if (search) {
      if (typeof searchRequest === 'function') {
        searchRequest(search);
      } else {
        setShownOptions(
          options?.filter((option) =>
            searchFilter ? searchFilter(option, search) : defaultFilter(option)
          ) || []
        );
      }
    } else {
      setShownOptions(options);
    }
  }, [search, options, defaultFilter, searchFilter, searchRequest]);

  const handleRadioGroupChange = (e) => {
    setPreState([]);
    radioGroupData.onRadioGroupChange(e);
  };

  return (
    <div
      className={styles.dropdownWindow}
      style={style}
      ref={dropdownWindowRef}
    >
      {!hideSearch && (
        <Input
          value={search}
          onChange={onSearchChange}
          className={styles.input}
          autoFocus={searchInputAutoFocus}
        />
      )}
      <div className={styles.actions}>
        {selectAllAvailable && (
          <span
            className={styles.action}
            onClick={() => {
              if (shownOptions && !loading) {
                setPreState(shownOptions.map((option) => option.id));
              }
            }}
          >
            Выделить все
          </span>
        )}
        {deselectAllAvailable && (
          <span className={styles.action} onClick={() => setPreState([])}>
            Снять все
          </span>
        )}
      </div>
      {radioGroupData && (
        <div className={styles.radioButtons}>
          <Radio.Group
            onChange={handleRadioGroupChange}
            defaultValue={radioGroupData.defaultValue}
          >
            {radioGroupData.buttons.map((button) => (
              <Fragment key={button.value}>
                <Radio.Button value={button.value}>{button.text}</Radio.Button>
              </Fragment>
            ))}
          </Radio.Group>
        </div>
      )}
      {(selectAllAvailable || deselectAllAvailable) && (
        <Divider className={styles.divider} />
      )}
      <div className={styles.list}>
        <Spin spinning={loading || false} className={styles.loading}>
          {shownOptions?.map((option) => {
            const optionIsSelected = preSelected.includes(option.id);
            return (
              <div
                className={styles.option}
                key={option.id}
                onClick={() => {
                  if (optionIsSelected) {
                    preDeleteItem(option.id);
                  } else preAddItem(option.id);
                }}
              >
                {customOptionRender
                  ? customOptionRender(option)
                  : option[textField]}
                {optionIsSelected && (
                  <Icon type="check" className={styles.selectedIcon} />
                )}
              </div>
            );
          })}
          {!shownOptions?.length && !loading && (
            <Empty
              description={emptyText || 'Нет данных'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Spin>
      </div>
      <Divider className={styles.divider} />
      <DispexButton
        disabled={loading}
        onClick={onSubmit}
        type="secondary"
        className={classNames(styles.secondaryButton, styles.button)}
      >
        Применить
      </DispexButton>
    </div>
  );
};

export default memo(DropdownRender);
